import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { loadExchangeRates } from '../utils/exchangeRates';
import {
  detectRegionFromLocale,
  formatMoneyAmount,
  getCurrency,
  getRegion,
  loadStoredLocale,
  saveStoredLocale,
} from '../utils/locale';

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [region, setRegion] = useState('US');
  const [currency, setCurrency] = useState('USD');
  const [ready, setReady] = useState(false);
  const [ratesVersion, setRatesVersion] = useState(0);
  const [ratesSource, setRatesSource] = useState('static');

  useEffect(() => {
    const stored = loadStoredLocale();
    if (stored?.region && stored?.currency) {
      setRegion(stored.region);
      setCurrency(stored.currency);
    } else {
      const detected = detectRegionFromLocale();
      const detectedRegion = getRegion(detected);
      setRegion(detectedRegion.code);
      setCurrency(detectedRegion.currency);
      saveStoredLocale({
        region: detectedRegion.code,
        currency: detectedRegion.currency,
      });
    }
    setReady(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function refreshRates() {
      try {
        const result = await loadExchangeRates();
        if (cancelled) return;
        setRatesSource(result.source);
        setRatesVersion(version => version + 1);
      } catch {
        if (cancelled) return;
        setRatesSource('static');
      }
    }

    refreshRates();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const updateRegion = nextRegion => {
      const regionConfig = getRegion(nextRegion);
      setRegion(regionConfig.code);
      setCurrency(regionConfig.currency);
      saveStoredLocale({
        region: regionConfig.code,
        currency: regionConfig.currency,
      });
    };

    const updateCurrency = nextCurrency => {
      setCurrency(nextCurrency);
      saveStoredLocale({ region, currency: nextCurrency });
    };

    return {
      ready,
      region,
      currency,
      ratesSource,
      ratesVersion,
      regionConfig: getRegion(region),
      currencyConfig: getCurrency(currency),
      setRegion: updateRegion,
      setCurrency: updateCurrency,
      formatMoney: price => formatMoneyAmount(price, currency),
    };
  }, [region, currency, ready, ratesVersion, ratesSource]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}
